<script setup>
import { ref, computed, watch, useTemplateRef, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import SearchAPI from 'dashboard/api/search';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  docked: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const { width } = useWindowSize();
const { getPlainText, truncateMessage } = useMessageFormatter();

const currentChat = computed(() => store.getters.getSelectedChat);
const isMobile = computed(() => width.value < 640);

const searchQuery = ref('');
const startDate = ref('');
const endDate = ref('');
const showDateFilters = ref(false);
const results = ref([]);
const isFetching = ref(false);
const hasSearched = ref(false);

const searchInput = useTemplateRef('searchInput');

const isSearchValid = value => value.length > 1 || /^[0-9]+$/.test(value);

const hasActiveDateFilter = computed(() =>
  Boolean(startDate.value || endDate.value)
);

const normalizedDateFilters = computed(() => ({
  startDate: startDate.value || undefined,
  endDate: endDate.value || startDate.value || undefined,
}));

const canSearch = value => isSearchValid(value) || hasActiveDateFilter.value;

const clearResults = () => {
  results.value = [];
  hasSearched.value = false;
  isFetching.value = false;
};

const fetchResults = async value => {
  if (!currentChat.value?.id) {
    return;
  }

  isFetching.value = true;
  hasSearched.value = true;

  try {
    const { data } = await SearchAPI.messages({
      q: value,
      page: 1,
      conversationId: currentChat.value.id,
      ...normalizedDateFilters.value,
    });
    results.value = data?.payload?.messages || [];
  } catch (error) {
    results.value = [];
  } finally {
    isFetching.value = false;
  }
};

const debouncedSearch = debounce(fetchResults, 300, false);

const runSearch = () => {
  const trimmedValue = searchQuery.value.trim();
  if (!canSearch(trimmedValue)) {
    clearResults();
    return;
  }
  debouncedSearch(trimmedValue);
};

const onInput = event => {
  searchQuery.value = event.target.value;
  runSearch();
};

const onDateFilterChange = () => {
  if (startDate.value && endDate.value && endDate.value < startDate.value) {
    endDate.value = startDate.value;
  }
  runSearch();
};

const clearSearch = () => {
  searchQuery.value = '';
  runSearch();
};

const clearDateFilters = () => {
  startDate.value = '';
  endDate.value = '';
  runSearch();
};

const toggleDateFilters = () => {
  showDateFilters.value = !showDateFilters.value;
};

const resetSearch = () => {
  searchQuery.value = '';
  startDate.value = '';
  endDate.value = '';
  showDateFilters.value = false;
  clearResults();
};

const closePanel = () => {
  emit('update:show', false);
};

const messagePreview = message => {
  if (!message) {
    return t('CONVERSATION.NO_CONTENT');
  }

  if (message.content) {
    return truncateMessage(getPlainText(message.content));
  }

  const subject = message.content_attributes?.email?.subject;
  if (subject) {
    return truncateMessage(subject);
  }

  const audioAttachment = message.attachments?.find(
    attachment => attachment.file_type === 'audio'
  );

  if (audioAttachment?.transcribed_text) {
    return truncateMessage(audioAttachment.transcribed_text);
  }

  return t('CONVERSATION.NO_CONTENT');
};

const onSelectMessage = async message => {
  if (!message?.id || !currentChat.value?.id) {
    return;
  }

  const existingMessages = currentChat.value?.messages || [];
  const hasMessage = existingMessages.some(item => item.id === message.id);
  const oldestMessageId = existingMessages[0]?.id;

  if (!hasMessage && oldestMessageId && message.id < oldestMessageId) {
    await store.dispatch('fetchPreviousMessages', {
      conversationId: currentChat.value.id,
      after: message.id,
      before: oldestMessageId,
    });
  }

  await router.replace({
    query: {
      ...route.query,
      messageId: message.id,
    },
  });

  if (isMobile.value) {
    closePanel();
    await nextTick();
  }
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });
};

const formatMessageTime = timestamp => dynamicTime(timestamp, locale.value);

watch(
  () => props.show,
  async value => {
    if (!value) {
      resetSearch();
      return;
    }

    await nextTick();
    searchInput.value?.focus();
  }
);

watch(
  () => currentChat.value?.id,
  () => {
    resetSearch();
  }
);
</script>

<template>
  <aside
    v-if="show"
    class="fixed inset-0 z-50 flex flex-col w-full h-full bg-n-solid-1 border-l border-n-weak sm:w-80 md:w-96"
    :class="
      docked
        ? 'sm:static sm:inset-auto sm:h-full sm:z-auto sm:flex-shrink-0'
        : 'sm:fixed sm:inset-y-0 sm:right-0 sm:left-auto'
    "
  >
    <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
      <Button
        ghost
        slate
        sm
        icon="i-lucide-chevron-left"
        :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.CLOSE')"
        @click="closePanel"
      />
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ $t('CONVERSATION.SEARCH.TITLE') }}
      </h3>
    </div>
    <div class="flex flex-col gap-4 px-4 py-4 min-h-0 flex-1">
      <div
        class="flex items-center gap-2 h-10 px-3 rounded-lg border bg-n-alpha-black2 transition-colors border-n-weak"
      >
        <fluent-icon icon="search" aria-hidden="true" class="text-n-slate-10" />
        <input
          ref="searchInput"
          type="text"
          class="reset-base outline-none w-full m-0 bg-transparent border-transparent shadow-none text-sm text-n-slate-12 placeholder:text-n-slate-9"
          :placeholder="$t('CONVERSATION.MESSAGE_SEARCH.PLACEHOLDER')"
          :value="searchQuery"
          @input="onInput"
        />
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-12"
          :class="{ 'text-n-brand': hasActiveDateFilter }"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.DATE_FILTER')"
          @click="toggleDateFilters"
        >
          <fluent-icon icon="calendar" size="14" />
        </button>
        <button
          v-if="searchQuery"
          type="button"
          class="text-n-slate-10 hover:text-n-slate-12"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.CLEAR')"
          @click="clearSearch"
        >
          <fluent-icon icon="dismiss" size="14" />
        </button>
      </div>
      <div
        v-if="showDateFilters || hasActiveDateFilter"
        class="grid grid-cols-2 gap-2"
      >
        <label class="flex min-w-0 flex-col gap-1 text-xs text-n-slate-11">
          <span>{{ $t('CONVERSATION.MESSAGE_SEARCH.START_DATE') }}</span>
          <input
            v-model="startDate"
            type="date"
            class="reset-base h-9 w-full min-w-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
            @change="onDateFilterChange"
          />
        </label>
        <label class="flex min-w-0 flex-col gap-1 text-xs text-n-slate-11">
          <span>{{ $t('CONVERSATION.MESSAGE_SEARCH.END_DATE') }}</span>
          <input
            v-model="endDate"
            type="date"
            class="reset-base h-9 w-full min-w-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
            @change="onDateFilterChange"
          />
        </label>
        <button
          v-if="hasActiveDateFilter"
          type="button"
          class="col-span-2 justify-self-start text-xs text-n-slate-11 hover:text-n-slate-12"
          @click="clearDateFilters"
        >
          {{ $t('CONVERSATION.MESSAGE_SEARCH.CLEAR_FILTERS') }}
        </button>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div v-if="isFetching" class="px-1 py-2 text-sm text-n-slate-11">
          {{ $t('CONVERSATION.MESSAGE_SEARCH.SEARCHING') }}
        </div>
        <div
          v-else-if="!searchQuery && !hasActiveDateFilter"
          class="px-1 py-2 text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION.MESSAGE_SEARCH.HELPER') }}
        </div>
        <div
          v-else-if="!results.length && hasSearched"
          class="px-1 py-2 text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION.MESSAGE_SEARCH.NO_RESULTS') }}
        </div>
        <ul v-else-if="results.length" class="divide-y divide-n-weak">
          <li v-for="message in results" :key="message.id">
            <button
              type="button"
              class="flex flex-col w-full gap-1 px-1 py-2 text-left hover:bg-n-slate-2"
              @click="onSelectMessage(message)"
            >
              <div
                class="flex items-center justify-between text-xs text-n-slate-10"
              >
                <span>
                  {{ message.sender?.name || $t('CONVERSATION.BOT') }}
                </span>
                <span>
                  {{ formatMessageTime(message.created_at) }}
                </span>
              </div>
              <p class="text-sm text-n-slate-12 break-words">
                {{ messagePreview(message) }}
              </p>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </aside>
</template>
